import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Check, X } from "lucide-react";
import api from "@/lib/api";

export default function FeatureReupload() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [invalid, setInvalid] = useState(false);
    const [meta, setMeta] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [done, setDone] = useState(false);
    const [preview, setPreview] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/features/reupload/${token}`);
                if (alive) setMeta(data);
            } catch {
                if (alive) setInvalid(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [token]);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/features/reupload/${token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data.photo_url);
            setDone(true);
            toast.success("Photo received. Thank you.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    if (loading) return (
        <main className="pt-40 min-h-screen text-center text-[color:var(--cream-muted)]" data-testid="reupload-loading">Loading…</main>
    );

    if (invalid) return (
        <main className="pt-40 pb-40 min-h-screen" data-testid="reupload-invalid">
            <div className="mx-auto max-w-2xl px-6 text-center">
                <p className="eyebrow">Link no longer valid</p>
                <h1 className="font-display text-5xl md:text-6xl mt-6">This upload link has expired.</h1>
                <p className="mt-8 text-[color:var(--cream-muted)]">If you were sent this by our editors, please reply to that email and we'll issue a fresh link.</p>
                <div className="mt-10"><Link to="/" className="btn-gold btn-gold-solid">Return home</Link></div>
            </div>
        </main>
    );

    if (done) return (
        <main className="pt-40 pb-40 min-h-screen" data-testid="reupload-thanks">
            <div className="mx-auto max-w-2xl px-6 text-center">
                <p className="eyebrow">Photo received</p>
                <h1 className="font-display text-6xl md:text-7xl mt-6">Thank you.</h1>
                <p className="mt-6 text-[color:var(--cream-muted)]">We've got the new photo for <strong>{meta.business_name}</strong>. Our editors will finish getting your feature ready.</p>
                {preview && (
                    <div className="mt-10 border border-[color:var(--espresso)] inline-block">
                        <img src={preview} alt="uploaded" className="max-w-full max-h-[420px] object-cover" data-testid="reupload-preview" />
                    </div>
                )}
                <div className="mt-10"><Link to="/" className="btn-gold btn-gold-solid">Return home</Link></div>
            </div>
        </main>
    );

    return (
        <main className="pt-32 pb-32 min-h-screen" data-testid="reupload-form">
            <div className="mx-auto max-w-2xl px-6">
                <p className="eyebrow">Photo upload</p>
                <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">
                    Send a photo for<br />
                    <em className="text-[color:var(--gold)]">{meta.business_name}</em>.
                </h1>
                <p className="mt-8 text-[color:var(--cream-muted)] text-lg">
                    Hi {meta.owner_name?.split(" ")[0] || "there"} — the photo you originally submitted didn't come through cleanly. Please pick one below so we can finish getting your feature ready.
                </p>
                {meta.already_uploaded && (
                    <div className="mt-6 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/[0.04] p-4 text-xs uppercase tracking-[0.24em] text-[color:var(--gold)]">
                        A photo is already on file — uploading below will replace it.
                    </div>
                )}
                <div className="mt-12 border border-[color:var(--espresso)] p-10">
                    <p className="text-[0.7rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-2">Your photo</p>
                    <p className="text-xs text-[color:var(--cream-muted)] italic mb-6">Horizontal shots work best. JPG, PNG, WebP or HEIC · up to 25 MB.</p>
                    <label className={"inline-flex items-center gap-2 btn-gold btn-gold-solid cursor-pointer " + (uploading ? "opacity-40 pointer-events-none" : "")} data-testid="reupload-picker">
                        <Upload size={14} />
                        {uploading ? "Uploading…" : "Choose photo"}
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="reupload-input" />
                    </label>
                </div>
                <p className="mt-10 text-[0.65rem] text-[color:var(--cream-muted)] text-center">This link is private to your submission and works one time.</p>
            </div>
        </main>
    );
}
